import { createRef, useEffect } from "react";

import { css } from "@compiled/react";
import { useInView } from "react-intersection-observer";

import { media } from "@atlaskit/primitives/responsive";

import { Adg4RaisedMedia } from "@/components/adg4/raised-media/src";
import { useAdg4TabsContext } from "@/components/adg4/tabs/src";
import {
    FeatureInfo,
    FeatureInfoProps
} from "@/patterns/feature-info/feature-info";

const imageStyle = css({
    paddingBlockEnd: 24,

    [media.above.md]: {
        paddingBlockStart: 24
    }
});

const videoSizing = css({
    display: "flex",
    [media.below.sm]: {
        height: "52vw"
    },
    [media.only.sm]: {
        height: "40vw"
    }
});

const videoStyle = css({
    maxWidth: "100%"
});

export const ContentTypeVideo = ({
    videoSrc,
    videoPreviewSrc,
    ...props
}: FeatureInfoProps & {
    videoSrc: string;
    videoPreviewSrc: string;
}) => {
    const { userSelected, goToNextTab } = useAdg4TabsContext();
    const { ref: containerRef, inView: sectionIsVisible } = useInView({
        initialInView: true,
        threshold: 0.15
    });
    const videoRef = createRef<HTMLVideoElement>();

    useEffect(() => {
        if (!sectionIsVisible) {
            if (videoRef.current) {
                videoRef.current.pause();
                videoRef.current.currentTime = 0;
            }
        } else if (videoRef.current) {
            videoRef.current.play().catch(() => {
                // Ignore error likely due to autoplay restrictions
            });
        }
    }, [sectionIsVisible, videoRef]);

    return (
        <FeatureInfo {...props} reverseLayout>
            <div ref={containerRef} css={imageStyle}>
                <Adg4RaisedMedia>
                    <div css={videoSizing}>
                        <video
                            css={videoStyle}
                            playsInline={true}
                            muted={true}
                            preload="auto"
                            poster={videoPreviewSrc}
                            autoPlay={true}
                            loop={false}
                            ref={videoRef}
                            onEnded={() => {
                                if (!userSelected) {
                                    goToNextTab();
                                }
                            }}
                        >
                            <source src={videoSrc} type={`video/webm`} />
                        </video>
                    </div>
                </Adg4RaisedMedia>
            </div>
        </FeatureInfo>
    );
};
